import React, { useState } from 'react';
import { formatDublinDateTime, formatDublinTime, formatDublinDate } from '../../utils/date';
import {
  useCreateCleaningAreaMutation,
  useUpdateCleaningAreaMutation,
  useCreateCleaningCompletionMutation,
} from '../../store/api/localApi';
import { CleaningAreaWithStatus, CleaningFrequency } from '../../types';
import { useToast } from '../../components/Toast';

// ─── helpers (shared with CleaningRecords) ────────────────────────────────────

/** Display order of cadences everywhere on the page: strip, select, chart. */
export const FREQUENCY_ORDER: CleaningFrequency[] = [
  'DAILY',
  'TWICE_DAILY',
  'WEEKLY',
  'FORTNIGHTLY',
  'MONTHLY',
];

export function frequencyLabel(f: CleaningFrequency): string {
  switch (f) {
    case 'DAILY':
      return 'Daily';
    case 'TWICE_DAILY':
      return 'Twice daily';
    case 'WEEKLY':
      return 'Weekly';
    case 'FORTNIGHTLY':
      return 'Fortnightly';
    case 'MONTHLY':
      return 'Monthly';
  }
}

const isDailyCadence = (f: CleaningFrequency) => f === 'DAILY' || f === 'TWICE_DAILY';

/** "Missed last week" — only for cadences whose window is not a day. */
function missedLabel(f: CleaningFrequency): string | null {
  switch (f) {
    case 'WEEKLY':
      return 'Missed last week';
    case 'FORTNIGHTLY':
      return 'Missed last fortnight';
    case 'MONTHLY':
      return 'Missed last month';
    default:
      return null;
  }
}

/** Done in the current window. TWICE_DAILY is never "done" for the button's
 *  purposes — the second clean must stay one tap away. */
const doneInWindow = (a: CleaningAreaWithStatus) => a.status === 'OK';

/**
 * "Done" means the whole window's work is in: one tick for most cadences, two
 * for a twice-daily line. The row, the chips and Hide done all use this, so a
 * twice-daily line offers Mark cleaned until its second tick and then reads
 * "2 today ✓ Done" like any other finished line (an owner tapped one nine
 * times because the button never went away).
 */
const fullyDone = (a: CleaningAreaWithStatus) =>
  a.frequency === 'TWICE_DAILY' ? (a.completionsInWindow ?? 0) >= 2 : doneInWindow(a);

/**
 * A toast a manager can act on. The server answers with machine codes
 * (VALIDATION, AREA_NOT_FOUND, Access denied, Internal server error); shown
 * raw they say nothing about which line or what to do. `verb` is what the
 * user was doing, so every sentence composes ("Couldn't remove …", "Something
 * went wrong marking cleaned …").
 */
interface SaveVerb {
  /** "save" — "Couldn't save …" */
  inf: string;
  /** "saving" — "Something went wrong saving …" */
  ing: string;
}
const SAVE: SaveVerb = { inf: 'save', ing: 'saving' };
const REMOVE: SaveVerb = { inf: 'remove', ing: 'removing' };
const MARK_CLEANED: SaveVerb = { inf: 'mark cleaned', ing: 'marking cleaned' };
const ADD: SaveVerb = { inf: 'add', ing: 'adding' };

function describeSaveError(err: unknown, what: string, verb: SaveVerb = SAVE): string {
  const e = err as {
    status?: number;
    data?: { error?: string; details?: Array<{ path?: unknown[]; message?: string }> };
  };
  const code = e?.data?.error;
  if (code === 'VALIDATION') {
    const d = e.data?.details?.[0];
    const field = Array.isArray(d?.path) ? String(d!.path[0]) : 'a field';
    return `Couldn't ${verb.inf} ${what}: ${field} ${d?.message ?? 'is invalid'}.`;
  }
  if (code === 'AREA_NOT_FOUND') return `${what} was already removed — refresh the sheet.`;
  if (e?.status === 403 || code === 'Access denied') {
    return `You don't have permission to ${verb.inf} ${what}.`;
  }
  if (e?.status === undefined) {
    return `Couldn't reach the server to ${verb.inf} ${what}. Check the connection and try again.`;
  }
  return `Something went wrong ${verb.ing} ${what}. Try again; if it keeps happening, contact support.`;
}

const FrequencyOptions: React.FC = () => (
  <>
    {FREQUENCY_ORDER.map((f) => (
      <option key={f} value={f}>
        {frequencyLabel(f)}
      </option>
    ))}
  </>
);

/** Rooms already in use at this location, case-insensitively de-duped so
 *  'Main Playroom' and 'Main playroom' offer one suggestion, not two groups. */
function knownZonesOf(areas: CleaningAreaWithStatus[]): string[] {
  return Array.from(
    areas.reduce((acc, a) => {
      const z = a.zone?.trim();
      if (z && !acc.has(z.toLowerCase())) acc.set(z.toLowerCase(), z);
      return acc;
    }, new Map<string, string>())
  )
    .map(([, v]) => v)
    .sort((a, b) => a.localeCompare(b));
}

/**
 * Group by room for display only — a room has no status of its own, no
 * frequency and nothing to sign off. Ungrouped lines sort last. Lines keep a
 * stable order inside a room so nothing jumps as it is ticked; the strip and
 * Hide done carry the "what's left" job.
 */
export function groupByZone<T extends { zone?: string }>(areas: T[]): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const a of areas) {
    const key = a.zone?.trim() || '';
    const bucket = groups.get(key);
    if (bucket) bucket.push(a);
    else groups.set(key, [a]);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === '') return 1;
    if (b === '') return -1;
    return a.localeCompare(b);
  });
}

// ─── AreaRow ──────────────────────────────────────────────────────────────────

interface AreaRowProps {
  area: CleaningAreaWithStatus;
  locationId: string;
  isAdmin: boolean;
  /** Rooms already in use here, offered as suggestions while editing. */
  knownZones: string[];
}

const AreaRow: React.FC<AreaRowProps> = ({ area, locationId, isAdmin, knownZones }) => {
  const [note, setNote] = useState('');
  const [productUsed, setProductUsed] = useState('');
  // Any line can take a note with its sign-off ("bleach running low", "tap
  // dripping") — the customer asked for a comment on the sheet, and the box is
  // always there, like the comments column on the paper sheet. Product used
  // stays a high-risk-only field.
  const [submitting, setSubmitting] = useState(false);
  const [updateArea] = useUpdateCleaningAreaMutation();
  const [createCompletion] = useCreateCleaningCompletionMutation();
  const { showError } = useToast();

  // Inline edit (admin). A manager who set the sheet up one level too high
  // ("Large Room" as an item) needs to rename and re-home lines in place, not
  // remove and re-create them and lose their cleaning history.
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(area.name);
  const [editZone, setEditZone] = useState(area.zone ?? '');
  const [editFrequency, setEditFrequency] = useState<CleaningFrequency>(area.frequency);
  const [editHighRisk, setEditHighRisk] = useState(area.isHighRisk);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const startEdit = () => {
    setEditName(area.name);
    setEditZone(area.zone ?? '');
    setEditFrequency(area.frequency);
    setEditHighRisk(area.isHighRisk);
    setEditing(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = editName.trim();
    if (!name) return;
    // Only what changed: the server records every key it receives in the
    // line's edit history, so sending all four would log "renamed" on a
    // frequency change. '' clears the room on the server.
    const zone = editZone.trim();
    const patch: {
      name?: string;
      zone?: string;
      frequency?: CleaningFrequency;
      isHighRisk?: boolean;
    } = {};
    if (name !== area.name) patch.name = name;
    if (zone !== (area.zone ?? '')) patch.zone = zone;
    if (editFrequency !== area.frequency) patch.frequency = editFrequency;
    if (editHighRisk !== area.isHighRisk) patch.isHighRisk = editHighRisk;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateArea({ locationId, areaId: area.id, ...patch }).unwrap();
      setEditing(false);
    } catch (err) {
      console.error('cleaning: save failed', err);
      showError(describeSaveError(err, `“${area.name}”`));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkCleaned = async () => {
    setSubmitting(true);
    try {
      await createCompletion({
        locationId,
        areaId: area.id,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(area.isHighRisk && productUsed ? { productUsed } : {}),
      }).unwrap();
      setNote('');
      setProductUsed('');
    } catch (err) {
      console.error('cleaning: mark cleaned failed', err);
      showError(describeSaveError(err, `“${area.name}”`, MARK_CLEANED));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    setRemoving(true);
    try {
      await updateArea({ locationId, areaId: area.id, isActive: false }).unwrap();
    } catch (err) {
      console.error('cleaning: remove failed', err);
      showError(describeSaveError(err, `“${area.name}”`, REMOVE));
    } finally {
      setRemoving(false);
    }
  };

  if (editing) {
    return (
      <form
        data-testid="cleaning-area-row"
        onSubmit={handleSaveEdit}
        className="bg-white rounded-lg border border-primary-300 shadow-sm px-4 py-3 space-y-3"
      >
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            data-testid={`cleaning-edit-name-${area.id}`}
            aria-label="What gets cleaned"
            placeholder="What gets cleaned, e.g. Floors, Toilets"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
            maxLength={120}
            className="flex-1 min-w-[180px] text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="text"
            data-testid={`cleaning-edit-zone-${area.id}`}
            aria-label="Room"
            placeholder="Room (optional)"
            list={`cleaning-zone-suggestions-${area.id}`}
            value={editZone}
            onChange={(e) => setEditZone(e.target.value)}
            maxLength={120}
            className="flex-1 min-w-[140px] text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <datalist id={`cleaning-zone-suggestions-${area.id}`}>
            {knownZones.map((z) => (
              <option key={z} value={z} />
            ))}
          </datalist>
          <select
            data-testid={`cleaning-edit-frequency-${area.id}`}
            aria-label="Frequency"
            value={editFrequency}
            onChange={(e) => setEditFrequency(e.target.value as CleaningFrequency)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <FrequencyOptions />
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              data-testid={`cleaning-edit-highrisk-${area.id}`}
              checked={editHighRisk}
              onChange={(e) => setEditHighRisk(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            High risk
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid={`cleaning-edit-cancel-${area.id}`}
            onClick={() => setEditing(false)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={`cleaning-edit-save-${area.id}`}
            disabled={saving || !editName.trim()}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    );
  }

  // ── Status wording. The backend enum stays OK/DUE/OVERDUE; people read
  //    To do / Done by … / n today. Daily lines never say "missed": a Mon–Fri
  //    service would read every daily line as missed every Monday morning.
  const twiceDaily = area.frequency === 'TWICE_DAILY';
  const count = area.completionsInWindow ?? 0;
  const done = fullyDone(area);
  let statusText: string;
  if (twiceDaily) {
    statusText = count > 0 ? `${count} today` : 'To do';
  } else if (done && area.lastCleanedByName && area.lastCleanedAt) {
    const when = isDailyCadence(area.frequency)
      ? formatDublinTime(area.lastCleanedAt)
      : formatDublinDateTime(area.lastCleanedAt);
    statusText = `Done by ${area.lastCleanedByName}, ${when}`;
  } else {
    statusText = 'To do';
  }
  // "Missed" needs a previous window to have been missed. A line that has never
  // been ticked (a manager recreating 44 weekly lines) is simply To do — a wall
  // of red on a fresh sheet would be false and would teach people to ignore it.
  const missed =
    area.status === 'OVERDUE' && area.lastCleanedAt ? missedLabel(area.frequency) : null;
  const showPrimaryButton = !done;
  const muted = done;

  return (
    <div
      data-testid="cleaning-area-row"
      className={`bg-white rounded-lg border shadow-sm px-4 py-3 space-y-2 ${
        muted ? 'border-gray-100' : 'border-gray-200'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* Name */}
        <span className={`font-medium text-sm ${muted ? 'text-gray-500' : 'text-gray-900'}`}>
          {area.name}
        </span>

        {/* Room. Also shown on the row, not only as a group heading — the
            chart and the cadence filter break the grouping, and an item
            called "Floor" is ambiguous without it. */}
        {area.zone && (
          <span data-testid="cleaning-area-zone" className="text-xs text-gray-500">
            {area.zone}
          </span>
        )}

        {/* Cadence */}
        <span className="text-xs text-gray-500">{frequencyLabel(area.frequency)}</span>

        {/* High-risk chip */}
        {area.isHighRisk && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            High risk
          </span>
        )}

        {/* Status, in words */}
        <span
          data-testid={`cleaning-status-${area.id}`}
          className={`text-xs ${done ? 'text-emerald-700' : 'text-amber-700 font-medium'}`}
        >
          {statusText}
        </span>
        {/* The note is from the LATEST sign-off. Once the line is due again it
            is a report from a past clean, not the state of the line now, so it
            carries its date. */}
        {area.lastNote && (
          <span
            data-testid={`cleaning-last-note-${area.id}`}
            className="text-xs text-gray-600 italic"
            title={area.lastCleanedByName ? `Note from ${area.lastCleanedByName}` : 'Note'}
          >
            “{area.lastNote}”
            {!done && area.lastCleanedAt ? ` (${formatDublinDate(area.lastCleanedAt)})` : ''}
          </span>
        )}
        {missed && (
          <span
            data-testid={`cleaning-missed-${area.id}`}
            className="text-xs font-medium text-rose-700"
          >
            {missed}
          </span>
        )}

        <div className="ml-auto flex items-center gap-2">
          {showPrimaryButton ? (
            <button
              type="button"
              data-testid={`cleaning-mark-cleaned-${area.id}`}
              onClick={handleMarkCleaned}
              disabled={submitting}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {submitting ? 'Saving…' : 'Mark cleaned'}
            </button>
          ) : (
            <>
              {/* Done means done: no primary button to tap twice by accident.
                  "Clean again" stays for high-risk repeats and corrections. */}
              <span
                data-testid={`cleaning-done-${area.id}`}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-emerald-800 bg-emerald-50 rounded-md"
              >
                ✓ Done
              </span>
              <button
                type="button"
                data-testid={`cleaning-clean-again-${area.id}`}
                onClick={handleMarkCleaned}
                disabled={submitting}
                className="text-xs text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
              >
                {submitting ? 'Saving…' : 'Clean again'}
              </button>
            </>
          )}

          {/* Admin: edit in place, or remove from the sheet (history is kept) */}
          {isAdmin && (
            <>
              <button
                type="button"
                data-testid={`cleaning-edit-${area.id}`}
                onClick={startEdit}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Edit
              </button>
              <button
                type="button"
                data-testid={`cleaning-deactivate-${area.id}`}
                onClick={handleDeactivate}
                disabled={removing}
                title="Takes it off the sheet. Past cleaning records are kept."
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                {removing ? 'Removing…' : 'Remove'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Note with the next sign-off (any line); product used (high-risk only) */}
      <div className="flex flex-wrap gap-3 pt-1">
        <input
          type="text"
          data-testid={`cleaning-completion-note-${area.id}`}
          placeholder="Note (optional), saved with Mark cleaned"
          value={note}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
          className="flex-1 min-w-[160px] text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {area.isHighRisk && (
          <input
            type="text"
            data-testid={`cleaning-completion-product-${area.id}`}
            placeholder="Product used (optional)"
            value={productUsed}
            onChange={(e) => setProductUsed(e.target.value)}
            className="flex-1 min-w-[160px] text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        )}
      </div>
    </div>
  );
};

// ─── CleaningSheet ────────────────────────────────────────────────────────────

interface CleaningSheetProps {
  locationId: string;
  isAdmin: boolean;
  areas: CleaningAreaWithStatus[];
}

const CleaningSheet: React.FC<CleaningSheetProps> = ({ locationId, isAdmin, areas }) => {
  const [filter, setFilter] = useState<CleaningFrequency | null>(null);
  const [hideDone, setHideDone] = useState(false);

  // Add-item form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newZone, setNewZone] = useState('');
  const [newFrequency, setNewFrequency] = useState<CleaningFrequency>('DAILY');
  const [newHighRisk, setNewHighRisk] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [createArea] = useCreateCleaningAreaMutation();
  const { showError } = useToast();

  const knownZones = knownZonesOf(areas);

  // One chip per cadence present here: "Daily 12 / 45 done".
  const cadences = FREQUENCY_ORDER.filter((f) => areas.some((a) => a.frequency === f)).map((f) => {
    const lines = areas.filter((a) => a.frequency === f);
    return { frequency: f, total: lines.length, done: lines.filter(fullyDone).length };
  });

  const visible = areas.filter(
    (a) => (!filter || a.frequency === filter) && !(hideDone && fullyDone(a))
  );
  const groupedAreas = groupByZone(visible);

  const toggleFilter = (f: CleaningFrequency) => setFilter((cur) => (cur === f ? null : f));

  const openAddForm = () => {
    // Adding while looking at the Weekly chip almost always means a weekly line.
    if (!showAddForm) setNewFrequency(filter ?? 'DAILY');
    setShowAddForm((prev) => !prev);
  };

  const handleAddArea = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAddSubmitting(true);
    try {
      await createArea({
        locationId,
        name: newName.trim(),
        zone: newZone.trim() || undefined,
        frequency: newFrequency,
        isHighRisk: newHighRisk,
      }).unwrap();
      setNewName('');
      setNewZone('');
      setNewFrequency('DAILY');
      setNewHighRisk(false);
      setShowAddForm(false);
    } catch (err) {
      console.error('cleaning: add failed', err);
      showError(describeSaveError(err, `“${newName.trim()}”`, ADD));
    } finally {
      setAddSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        {/* Summary strip: chips are the counts AND the filter. */}
        {areas.length > 0 && (
          <div data-testid="cleaning-summary" className="flex flex-wrap items-center gap-2">
            {cadences.map(({ frequency, total, done }) => (
              <button
                key={frequency}
                type="button"
                data-testid={`cleaning-filter-${frequency}`}
                aria-pressed={filter === frequency}
                onClick={() => toggleFilter(frequency)}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  filter === frequency
                    ? 'bg-primary-600 text-white border-primary-600'
                    : done === total
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {frequencyLabel(frequency)} {done} / {total} done
              </button>
            ))}
            <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer ml-1">
              <input
                type="checkbox"
                data-testid="cleaning-hide-done"
                checked={hideDone}
                onChange={(e) => setHideDone(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Hide done
            </label>
          </div>
        )}
        {isAdmin && (
          <button
            type="button"
            data-testid="cleaning-add-area"
            onClick={openAddForm}
            className="ml-auto inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {showAddForm ? 'Cancel' : '+ Add item'}
          </button>
        )}
      </div>

      {/* Add item form (admin only) */}
      {isAdmin && showAddForm && (
        <form
          onSubmit={handleAddArea}
          className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3"
        >
          <h2 className="text-sm font-semibold text-gray-800">New cleaning item</h2>
          <p className="text-xs text-gray-500">
            One line per thing that gets cleaned — Floors, Tables, Toilets — with the room it is in
            and how often. Staff tick each line as they do it.
          </p>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              data-testid="cleaning-area-name-input"
              placeholder="What gets cleaned, e.g. Floors, Toilets"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
              maxLength={120}
              className="flex-1 min-w-[180px] text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="text"
              data-testid="cleaning-area-zone-input"
              placeholder="Room (optional)"
              list="cleaning-zone-suggestions"
              value={newZone}
              onChange={(e) => setNewZone(e.target.value)}
              maxLength={120}
              className="flex-1 min-w-[140px] text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {/* Suggest rooms already in use here, so 'Main Playroom' and
                'Main playroom' don't become two groups. */}
            <datalist id="cleaning-zone-suggestions">
              {knownZones.map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
            <select
              data-testid="cleaning-area-frequency"
              value={newFrequency}
              onChange={(e) => setNewFrequency(e.target.value as CleaningFrequency)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <FrequencyOptions />
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="cleaning-area-highrisk"
                checked={newHighRisk}
                onChange={(e) => setNewHighRisk(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              High risk
            </label>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="cleaning-area-submit"
              disabled={addSubmitting || !newName.trim()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {addSubmitting ? 'Saving…' : 'Add item'}
            </button>
          </div>
        </form>
      )}

      {/* The sheet */}
      {areas.length === 0 ? (
        <p data-testid="cleaning-empty" className="text-sm text-gray-500 py-8 text-center">
          Nothing on the sheet yet. Add one line per thing that gets cleaned — Floors, Tables,
          Toilets — with the room it is in and how often.
        </p>
      ) : visible.length === 0 ? (
        <p data-testid="cleaning-all-done" className="text-sm text-emerald-700 py-8 text-center">
          Everything here is done.
        </p>
      ) : (
        <div className="space-y-5">
          {groupedAreas.map(([zone, zoneAreas]) => (
            <div key={zone || '__ungrouped'} className="space-y-3">
              {/* Only label a group that HAS a room. An ungrouped list must
                  look exactly as it did before this field existed. */}
              {zone && (
                <h3
                  data-testid="cleaning-zone-heading"
                  className="text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  {zone}
                </h3>
              )}
              {zoneAreas.map((area) => (
                <AreaRow
                  key={area.id}
                  area={area}
                  locationId={locationId}
                  isAdmin={isAdmin}
                  knownZones={knownZones}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CleaningSheet;
